const FORM_WEBHOOK_URL =
  'https://script.google.com/macros/s/AKfycbygKzfQGsa9Nejb4eSj_Nz6SL8-EcFabTmJW8tf6keOTFadLBd8yzp2sR9UAFpHSs3p/exec'

export const HONEYPOT_FIELD = 'contact_url_2'

const MIN_COMPLETION_MS = 3000

const FIELD_LIMITS: Record<string, number> = {
  name: 100,
  organisation: 150,
  contact: 200,
  message: 2000,
  reason: 2000,
  source: 200,
}

interface SubmitRequestInput {
  requestType: string
  values: Record<string, string>
  honeypot: string
  formStartedAt: number
}

function normalizeText(value: string): string {
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/\r\n?/g, '\n')
    .trim()
}

function containsLink(value: string): boolean {
  return (
    /\bhttps?:\/\/\S+/i.test(value) ||
    /\bwww\.\S+/i.test(value) ||
    /\b[a-z0-9-]+\.(?:com|net|org|io|co|ru|de|uk|info|biz)\b/i.test(value)
  )
}

function validateValues(values: Record<string, string>): Record<string, string> {
  const normalized = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, normalizeText(value)]),
  )

  const required = ['name', 'organisation', 'contact']
  const messageKey = 'message' in normalized ? 'message' : 'reason'
  required.push(messageKey)

  for (const key of required) {
    if (!normalized[key]) {
      throw new Error('Please complete all required fields.')
    }
  }

  for (const [key, limit] of Object.entries(FIELD_LIMITS)) {
    if ((normalized[key] || '').length > limit) {
      throw new Error(`Please shorten the ${key} field to ${limit} characters or fewer.`)
    }
  }

  for (const key of ['name', 'organisation', 'message', 'reason', 'source']) {
    const value = normalized[key] || ''
    if (/<[^>]*>/i.test(value) || /\b(?:javascript|data|vbscript|file):/i.test(value)) {
      throw new Error('HTML and executable links are not accepted.')
    }
    if (containsLink(value)) {
      throw new Error('Please remove web links from the form before submitting.')
    }
  }

  return normalized
}

export async function submitFormRequest({
  requestType,
  values,
  honeypot,
  formStartedAt,
}: SubmitRequestInput): Promise<void> {
  if (Date.now() - formStartedAt < MIN_COMPLETION_MS) {
    throw new Error('Please take a moment to review the form before submitting.')
  }

  const normalized = validateValues(values)
  const searchParams = new URLSearchParams(window.location.search)

  const payload: Record<string, string> = {
    requestType,
    name: normalized.name,
    organisation: normalized.organisation,
    contact: normalized.contact,
    message: normalized.message || normalized.reason,
    source: requestType === 'Screening Access' ? '' : normalized.source || '',
    page: window.location.href.slice(0, 500),
    ref: (searchParams.get('ref') || '').slice(0, 200),
    formStartedAt: String(formStartedAt),
    [HONEYPOT_FIELD]: honeypot,
  }

  await postToGoogleSheets(payload)
}

async function postToGoogleSheets(payload: Record<string, string>): Promise<void> {
  const body = new URLSearchParams(payload)

  try {
    await fetch(FORM_WEBHOOK_URL, {
      method: 'POST',
      mode: 'no-cors',
      credentials: 'omit',
      redirect: 'follow',
      body,
    })
  } catch {
    throw new Error(
      'The request could not reach the response service. Please check your connection and try again.',
    )
  }
}
